import { useState, useMemo, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Map as MapIcon, 
  Utensils, 
  Bed, 
  Calendar, 
  Compass,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Menu,
  X,
  Search,
  Plus,
  Trash2,
  Navigation
} from 'lucide-react';
import { CHEESERIES, ACCOMMODATIONS } from './lib/data';
import { REGION_HUBS, STATE_HUBS, getRegionHubForLocation, getStateHub, type RegionHubKey } from './lib/trailHubs';
import { SCOPE } from './lib/brandScope';
import { Cheesery, Accommodation, ItineraryItem, CheeseStyle, MilkType, USState } from './types';
import { FAQ } from './lib/faq';
import { cn } from './lib/utils';
import { routeDistanceMi } from './utils/geo';
import { bookingSearchUrl } from './utils/booking';

/**
 * MapView pulls in Leaflet (~45KB gzipped) and react-leaflet. Lazy-loading it
 * keeps that cost off the initial page load; the map renders on the home tab
 * and inside the itinerary sidebar, both of which show a subtle skeleton
 * while the chunk fetches.
 */
const MapView = lazy(() => import('./components/MapView'));

/** Tiny placeholder shown while the MapView chunk is fetching. */
function MapSkeleton({ label = 'Loading map\u2026' }: { label?: string }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-sidebar/50">
      <div className="flex flex-col items-center gap-3 text-ink-light">
        <div className="w-8 h-8 border-2 border-border border-t-accent rounded-full animate-spin" />
        <span className="text-[10px] font-bold uppercase tracking-widest">{label}</span>
      </div>
    </div>
  );
}

const CHEESE_STYLES: CheeseStyle[] = ['Cheddar', 'Aged Hard', 'Soft Ripened', 'Blue', 'Fresh', 'Alpine'];
const MILK_TYPES: MilkType[] = ['Cow', 'Goat', 'Sheep', 'Mixed'];

/** Short, human-friendly names for the full US state enum. */
const STATE_LABEL: Record<USState, string> = {
  VT: 'Vermont',
  ME: 'Maine',
  NH: 'New Hampshire',
  MA: 'Massachusetts',
  NY: 'New York',
};
const AVAILABLE_STATES = STATE_HUBS.map((hub) => hub.state);

type AppTab = 'map' | 'trails' | 'cheeses' | 'stays' | 'itinerary';

/**
 * Two-way mapping between the in-app tab id and the URL path.
 * `/` is the canonical landing route and resolves to the map tab, which is
 * where the homepage hero + editorial content currently lives.
 */
const TAB_TO_PATH: Record<AppTab, string> = {
  map: '/',
  trails: '/trails',
  cheeses: '/cheeses',
  stays: '/stays',
  itinerary: '/plan',
};

const PATH_TO_TAB: Record<string, AppTab> = {
  '/': 'map',
  '/map': 'map',
  '/trails': 'trails',
  '/cheeses': 'cheeses',
  '/stays': 'stays',
  '/plan': 'itinerary',
};

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab: AppTab = PATH_TO_TAB[location.pathname] ?? 'map';
  const setActiveTab = (id: AppTab) => {
    const target = TAB_TO_PATH[id];
    if (location.pathname !== target) navigate(target);
  };
  const [selectedCheesery, setSelectedCheesery] = useState<Cheesery | null>(null);
  const [selectedStay, setSelectedStay] = useState<Accommodation | null>(null);
  const [itinerary, setItinerary] = useState<ItineraryItem[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Filters — shared across header search, cheeses tab, and stays tab.
  const [searchQuery, setSearchQuery] = useState('');
  const [styleFilter, setStyleFilter] = useState<CheeseStyle | ''>('');
  const [milkFilter, setMilkFilter] = useState<MilkType | ''>('');
  const [stateFilter, setStateFilter] = useState<USState | ''>('');
  const [regionFilter, setRegionFilter] = useState<RegionHubKey | ''>('');

  const searchMatches = (haystack: string) =>
    !searchQuery || haystack.toLowerCase().includes(searchQuery.toLowerCase());

  const filteredCheeseries = useMemo(() => {
    return CHEESERIES.filter(c => {
      const region = getRegionHubForLocation(c.id);
      const matchesSearch = searchMatches(
        `${c.name} ${c.description} ${c.city} ${c.state} ${region?.title ?? ''} ${c.styles.join(' ')} ${c.milkTypes.join(' ')}`
      );
      const matchesStyle = styleFilter ? c.styles.includes(styleFilter) : true;
      const matchesMilk = milkFilter ? c.milkTypes.includes(milkFilter) : true;
      const matchesState = stateFilter ? c.state === stateFilter : true;
      const matchesRegion = regionFilter ? region?.key === regionFilter : true;
      return matchesSearch && matchesStyle && matchesMilk && matchesState && matchesRegion;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, styleFilter, milkFilter, stateFilter, regionFilter]);

  const filteredStays = useMemo(() => {
    return ACCOMMODATIONS.filter(s => {
      const region = getRegionHubForLocation(s.id);
      const matchesSearch = searchMatches(
        `${s.name} ${s.description} ${s.city} ${s.state} ${region?.title ?? ''} ${s.style} ${(s.amenities || []).join(' ')}`
      );
      const matchesState = stateFilter ? s.state === stateFilter : true;
      const matchesRegion = regionFilter ? region?.key === regionFilter : true;
      return matchesSearch && matchesState && matchesRegion;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, stateFilter, regionFilter]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setStyleFilter('');
    setMilkFilter('');
    setStateFilter('');
    setRegionFilter('');
  };

  const exploreState = (state: USState, tab: 'cheeses' | 'stays' = 'cheeses') => {
    setStateFilter(state);
    setRegionFilter('');
    setActiveTab(tab);
  };

  const exploreRegion = (regionKey: RegionHubKey, tab: 'cheeses' | 'stays' = 'cheeses') => {
    const hub = REGION_HUBS.find((region) => region.key === regionKey);
    if (!hub) return;
    setStateFilter(hub.state);
    setRegionFilter(regionKey);
    setActiveTab(tab);
  };

  const addToItinerary = (id: string) => {
    if (itinerary.some(item => item.locationId === id)) return;
    const newItem: ItineraryItem = {
      id: Math.random().toString(36).substr(2, 9),
      locationId: id,
      day: 1,
      order: itinerary.length,
    };
    setItinerary([...itinerary, newItem]);
  };

  const removeFromItinerary = (id: string) => {
    setItinerary(itinerary.filter(item => item.id !== id));
  };

  const moveItinerary = (id: string, direction: -1 | 1) => {
    setItinerary((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      // keep `order` field consistent with array position
      return next.map((it, i) => ({ ...it, order: i }));
    });
  };

  const setItineraryNote = (id: string, notes: string) => {
    setItinerary((prev) =>
      prev.map((item) => (item.id === id ? { ...item, notes } : item))
    );
  };

  /**
   * Resolve each itinerary entry to its underlying location, preserving
   * order. Null entries are filtered out — they can only appear transiently
   * when a location id no longer exists in the data set.
   */
  const itineraryStops = useMemo(() => {
    return itinerary
      .map((it) => {
        const loc = [...CHEESERIES, ...ACCOMMODATIONS].find(
          (l) => l.id === it.locationId
        );
        return loc ? { item: it, location: loc } : null;
      })
      .filter(<T,>(v: T | null): v is T => v !== null);
  }, [itinerary]);

  /** Straight-line route distance and a conservative drive-time estimate. */
  const routeStats = useMemo(() => {
    const pts = itineraryStops.map((s) => ({ lat: s.location.lat, lng: s.location.lng }));
    const miles = routeDistanceMi(pts);
    // Assume a rural-New-England blended average around 38 mph when
    // accounting for two-lane roads and in-town slowdowns, then inflate
    // straight-line distance by ~1.35x for real road geometry.
    const drivingMiles = miles * 1.35;
    const hours = drivingMiles / 38;
    return { miles, drivingMiles, hours };
  }, [itineraryStops]);

  /**
   * Build and trigger download of a GPX 1.1 document with one waypoint per
   * itinerary stop, in order. Works in any modern browser without a backend.
   */
  const exportGpx = () => {
    if (!itineraryStops.length) return;
    const escape = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const waypoints = itineraryStops
      .map(
        ({ location }, i) => `  <wpt lat="${location.lat}" lon="${location.lng}">
    <name>${i + 1}. ${escape(location.name)}</name>
    <desc>${escape(`${location.city}, ${location.state} — ${location.type === 'cheesery' ? 'Cheesery' : 'Stay'}`)}</desc>
  </wpt>`
      )
      .join('\n');
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="The Curd Compass" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>Curd Compass itinerary</name>
    <time>${new Date().toISOString()}</time>
  </metadata>
${waypoints}
</gpx>`;
    const blob = new Blob([gpx], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `curd-compass-itinerary-${new Date().toISOString().slice(0, 10)}.gpx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const NavItem = ({ id, label, icon: Icon }: { id: typeof activeTab, label: string, icon: any }) => (
    <button
      onClick={() => { setActiveTab(id); setMobileMenuOpen(false); }}
      className={cn(
        "flex items-center gap-3 w-full py-3 text-sm transition-all duration-300 relative group",
        activeTab === id ? "text-ink font-semibold" : "text-ink-light hover:text-ink"
      )}
    >
      <div className={cn(
        "w-5 h-5 flex items-center justify-center rounded transition-colors",
        activeTab === id ? "bg-accent text-white" : "bg-border text-ink-light group-hover:bg-accent/20"
      )}>
        <Icon size={12} />
      </div>
      <span className="flex-1 text-left">{label}</span>
      {activeTab === id && (
        <motion.div
          layoutId="nav-underline"
          className="absolute bottom-0 left-0 right-0 h-[1px] bg-accent"
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
        />
      )}
    </button>
  );

  return (
    <div className="h-screen w-full flex overflow-hidden bg-bg font-sans text-ink">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-64 bg-sidebar border-r border-border flex-col p-8 pb-4 shrink-0 overflow-y-auto">
        <div className="mb-12">
          <h1 className="text-2xl font-serif font-bold italic text-accent tracking-tighter">
            Curd Compass
          </h1>
          <p className="text-[10px] uppercase tracking-widest text-ink-light mt-1 font-bold">{SCOPE.label} Edition</p>
        </div>
        
        <nav className="flex-1 space-y-2">
          <NavItem id="map" label="Field Atlas" icon={MapIcon} />
          <NavItem id="trails" label="Trail Hubs" icon={Compass} />
          <NavItem id="cheeses" label="Artisan Cheeseries" icon={Utensils} />
          <NavItem id="stays" label="Where to Stay" icon={Bed} />
          <NavItem id="itinerary" label="The Itinerary" icon={Calendar} />
        </nav>

        <div className="pt-6 border-t border-border mt-auto">
          <p className="text-[9px] uppercase tracking-widest text-ink-light leading-relaxed">
            © {new Date().getFullYear()} The Curd Compass<br />Crafted in New England
          </p>
        </div>
      </aside>

      {/* Main Container */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="h-20 border-b border-border flex items-center justify-between px-6 md:px-10 shrink-0 bg-white/50 backdrop-blur-md z-40">
          <div className="flex items-center gap-4 lg:hidden">
            <h1 className="text-xl font-serif font-bold italic text-accent tracking-tighter">Curd Compass</h1>
          </div>

          <div className="flex-1 max-w-md hidden md:block">
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-light group-focus-within:text-accent transition-colors" size={14} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search creameries, towns, styles…"
                className="w-full pl-10 pr-4 py-2 bg-transparent border border-border rounded-full text-sm focus:outline-none focus:border-accent transition-all placeholder:text-ink-light/50"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-light hover:text-ink"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 text-ink"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </header>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, x: -260 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -260 }}
              className="fixed inset-0 z-[100] lg:hidden flex"
            >
              <div className="w-64 bg-sidebar border-r border-border p-8 flex flex-col">
                <div className="mb-12 flex items-center justify-between">
                  <h1 className="text-2xl font-serif font-bold italic text-accent italic tracking-tighter">Curd Compass</h1>
                  <button onClick={() => setMobileMenuOpen(false)}><X size={20}/></button>
                </div>
                <nav className="flex-1 space-y-2">
                  <NavItem id="map" label="Field Atlas" icon={MapIcon} />
                  <NavItem id="trails" label="Trail Hubs" icon={Compass} />
                  <NavItem id="cheeses" label="Artisan Cheeseries" icon={Utensils} />
                  <NavItem id="stays" label="Where to Stay" icon={Bed} />
                  <NavItem id="itinerary" label="The Itinerary" icon={Calendar} />
                </nav>
              </div>
              <div className="flex-1 bg-ink/20 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dashboard Content */}
        <main className="flex-1 overflow-y-auto bg-bg relative custom-scrollbar">
          <AnimatePresence mode="wait">
          {activeTab === 'map' && (
            <motion.div
              key="map"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-6 md:p-12 max-w-screen-2xl mx-auto w-full"
            >
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-12 items-center min-h-[70vh]">
                <div className="space-y-8">
                  <div className="space-y-4">
                    <span className="pill-minimal active">Field Atlas / {SCOPE.label}</span>
                    <h2 className="text-6xl md:text-8xl font-serif text-ink tracking-tight font-medium leading-[1.1]">
                      Artisan Flavor, <span className="italic font-light text-accent">Rooted in Earth.</span>
                    </h2>
                    <p className="text-xl text-ink-light max-w-lg leading-relaxed font-light">
                      {SCOPE.heroTagline}
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => setActiveTab('cheeses')}
                      className="px-10 py-4 bg-ink text-white rounded-full text-xs font-bold uppercase tracking-[0.2em] hover:bg-accent transition-all shadow-lg hover:shadow-accent/20"
                    >
                      Explore the Trail
                    </button>
                    <button 
                      onClick={() => setActiveTab('trails')}
                      className="px-10 py-4 border border-border rounded-full text-xs font-bold uppercase tracking-[0.2em] text-ink hover:bg-sidebar transition-all"
                    >
                      Choose a Region
                    </button>
                    <button
                      onClick={() => setActiveTab('itinerary')}
                      className="px-10 py-4 border border-border rounded-full text-xs font-bold uppercase tracking-[0.2em] text-ink-light hover:text-ink hover:bg-sidebar transition-all"
                    >
                      Saved Journey ({itinerary.length})
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-8 pt-12 border-t border-border">
                    <div className="space-y-1 text-center sm:text-left">
                      <p className="text-4xl font-serif text-accent">{CHEESERIES.length}</p>
                      <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Makers</p>
                    </div>
                    <div className="space-y-1 text-center sm:text-left">
                      <p className="text-4xl font-serif text-accent">{STATE_HUBS.length}</p>
                      <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">State Hubs</p>
                    </div>
                    <div className="space-y-1 text-center sm:text-left">
                      <p className="text-4xl font-serif text-accent">{REGION_HUBS.length}</p>
                      <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Route Regions</p>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <div className="absolute -inset-4 bg-accent/5 rounded-[40px] rotate-2 group-hover:rotate-0 transition-transform duration-700" />
                  <div className="relative aspect-[4/5] bg-sidebar border border-border rounded-[32px] overflow-hidden shadow-2xl flex flex-col">
                    <div className="flex items-center justify-between p-6 pb-4 shrink-0">
                      <div className="flex items-center gap-2">
                        <MapIcon size={18} className="text-accent" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-ink">Trail Explorer</span>
                      </div>
                      <div className="px-3 py-1 bg-white border border-border rounded-full text-[9px] font-bold uppercase">{STATE_HUBS.map((h) => h.state).join(' · ')}</div>
                    </div>

                    <div className="flex-1 relative overflow-hidden">
                      <Suspense fallback={<MapSkeleton />}>
                        <MapView
                          cheeseries={CHEESERIES}
                          accommodations={ACCOMMODATIONS}
                          fitBounds
                          onAddToItinerary={addToItinerary}
                          itineraryIds={itinerary.map((i) => i.locationId)}
                          onSelect={(id, kind) => {
                            if (kind === 'cheesery') {
                              const c = CHEESERIES.find((x) => x.id === id);
                              if (c) setSelectedCheesery(c);
                            } else {
                              const s = ACCOMMODATIONS.find((x) => x.id === id);
                              if (s) setSelectedStay(s);
                            }
                          }}
                        />
                      </Suspense>
                    </div>

                    <div className="px-6 py-3 text-center border-t border-border shrink-0">
                      <p className="text-[10px] text-ink-light font-medium italic">
                        {CHEESERIES.length} cheeseries · {ACCOMMODATIONS.length} stays · click a pin to explore
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ------------------------------------------------------------
                  Editorial content below the hero. This is the primary SEO
                  surface: geo-targeted prose, semantic headings, and an
                  FAQPage that mirrors the JSON-LD block in index.html.
                  "Where the trail runs today" — data-driven chapter index.
                  Auto-expands as new state hubs are added via STATE_HUBS,
                  so the homepage scales to NH, MA, NY without copy rewrites.
                  ------------------------------------------------------------ */}
              <section className="mt-24 md:mt-32 max-w-5xl mx-auto space-y-10">
                <div className="max-w-3xl mx-auto space-y-4">
                  <span className="pill-minimal active">Where the trail runs today</span>
                  <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                    A <span className="italic font-light text-accent">field guide</span>, organized by chapter.
                  </h2>
                  <p className="text-lg text-ink-light leading-relaxed">
                    The Curd Compass is published as a set of editorial
                    chapters, one per state. Each chapter splits into driveable
                    route regions, pairs creameries with thoughtful places to
                    stay, and grows slowly as new trails are vetted.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {STATE_HUBS.map((hub) => {
                    const slug = `/${hub.slug}`;
                    const makerCount = CHEESERIES.filter((c) => c.state === hub.state).length;
                    const regionCount = REGION_HUBS.filter((r) => r.state === hub.state).length;

                    return (
                      <Link
                        key={hub.state}
                        to={slug}
                        className="group card-minimal p-6 md:p-8 bg-white/80 hover:bg-white transition-colors"
                      >
                        <div className="space-y-4">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                            {hub.eyebrow}
                          </p>
                          <h3 className="text-3xl md:text-4xl font-serif text-ink tracking-tight">
                            {hub.title}
                          </h3>
                          <p className="text-[15px] text-ink-light leading-relaxed">
                            {hub.subtitle}
                          </p>
                          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-ink-light font-bold">
                            <span>{makerCount} makers</span>
                            <span className="w-1 h-1 bg-border rounded-full" />
                            <span>{regionCount} route regions</span>
                          </div>
                          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink font-bold group-hover:text-accent transition-colors">
                            Read the chapter <ChevronRight size={14} />
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>

              {/* ------------------------------------------------------------
                  "The project" — evergreen editorial copy. Intentionally
                  state-agnostic so it reads the same whether we cover 2
                  states or 5. Specific state bodies live on each state hub.
                  ------------------------------------------------------------ */}
              <section className="mt-24 md:mt-32 max-w-3xl mx-auto space-y-6">
                <span className="pill-minimal active">The project</span>
                <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                  An independent, <span className="italic font-light text-accent">editorial</span> cheese guide.
                </h2>
                <p className="text-lg text-ink-light leading-relaxed">
                  The Curd Compass is a small, hand-built guide to the best
                  cheese trips in the Northeast. Every cheesery listed is a
                  real, operating business; every address, set of hours, and
                  visitor policy is cross-checked against state cheese
                  guilds, producer websites, and the occasional phone call.
                  No one pays for inclusion.
                </p>
                <p className="text-lg text-ink-light leading-relaxed">
                  We flag the difference between retail-friendly stops like
                  visitor centers and strictly production-only operations
                  whose cheese is sold through a nearby village store.
                  Paired with each creamery are hand-picked inns, B&amp;Bs,
                  and farm stays within practical driving distance &mdash; so
                  a weekend can be planned as one readable itinerary instead
                  of fifteen open browser tabs.
                </p>
                <p className="text-lg text-ink-light leading-relaxed">
                  The guide currently covers{' '}
                  <strong className="text-ink font-semibold">
                    {CHEESERIES.length} creameries
                  </strong>{' '}
                  and{' '}
                  <strong className="text-ink font-semibold">
                    {ACCOMMODATIONS.length} places to stay
                  </strong>{' '}
                  across {STATE_HUBS.length} state{STATE_HUBS.length === 1 ? '' : 's'}
                  {' '}and {REGION_HUBS.length} travel regions, and expands
                  slowly &mdash; one vetted chapter at a time.
                </p>
                <p className="text-[15px] text-ink-light leading-relaxed pt-2 border-t border-border mt-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-ink-light block mb-2">
                    Sister guide
                  </span>
                  Heading further south on a different trip? Our sister site, the{' '}
                  <a
                    href="https://pacheesetrail.com/"
                    rel="noopener"
                    className="text-accent font-semibold underline decoration-accent/40 underline-offset-2 hover:decoration-accent transition-colors"
                  >
                    Pennsylvania Cheese Trail
                  </a>
                  , picks up the thread through Amish-country creameries and
                  Lancaster County dairies at the same editorial standard.
                </p>
              </section>

              <section className="mt-24 md:mt-32 max-w-5xl mx-auto">
                <div className="max-w-3xl mx-auto space-y-4 mb-12 md:mb-16">
                  <span className="pill-minimal active">The Regions</span>
                  <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                    Cheesemaking regions <span className="italic font-light text-accent">on the trail</span>
                  </h2>
                  <p className="text-base text-ink-light leading-relaxed">
                    {REGION_HUBS.length} distinct micro-regions are represented on the trail today,
                    each with its own dairy character. Route your weekend
                    around one, or stitch two adjacent ones together from the
                    Trail Hubs chapter.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
                  <div className="space-y-3">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Champlain Valley &amp; Burlington, Vermont</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      The broad agricultural valley on Vermont&rsquo;s western
                      edge, centered on Burlington and Middlebury. Shelburne
                      Farms anchors the region with its 1,400-acre working
                      estate on Lake Champlain; Blue Ledge Farm in Salisbury
                      and the Willard Street Inn in Burlington&rsquo;s Hill
                      Section round out a classic weekend.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Mad River Valley &amp; Green Mountains</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      Rolling dairy country along Vermont&rsquo;s Route 100.
                      Von Trapp Farmstead in Waitsfield and the Pitcher Inn in
                      Warren sit in the same narrow valley; Sugarbush Farm
                      just east of Woodstock pairs maple tastings with its
                      waxed cheddars.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Northeast Kingdom, Vermont</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      Vermont&rsquo;s high, remote corner. Jasper Hill Farm in
                      Greensboro is the cellar of record for much of the
                      state&rsquo;s most-aged cheese. The farm itself is
                      closed to visitors for bio-security reasons, but
                      Willey&rsquo;s Store in Greensboro Village carries the
                      full range at the cave door.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Southern Vermont &amp; the Connecticut River Valley</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      Brattleboro, Grafton, Putney, and Plymouth Notch. Home
                      to Grafton Village Cheese&rsquo;s flagship Brattleboro
                      shop, Plymouth Artisan Cheese&rsquo;s historic
                      Coolidge-site factory, Crowley Cheese&rsquo;s
                      1882 handmade-curd operation, and Vermont Shepherd&rsquo;s
                      hillside sheep-milk aging cave.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Midcoast Maine</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      From Appleton and Pittsfield inland to Camden and
                      Rockland on the coast. Appleton Creamery, Crooked Face,
                      and Balfour Farm Dairy supply the midcoast&rsquo;s
                      farmers markets; Camden Harbour Inn makes a natural
                      base for the region.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Greater Portland &amp; Casco Bay</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      Pineland Farms in New Gloucester and Silvery Moon
                      Creamery at Smiling Hill Farm (Westbrook) are both easy
                      day-trips from Portland. The Press Hotel in the Old
                      Port and Inn by the Sea in Cape Elizabeth are the
                      obvious anchors.
                    </p>
                  </div>

                  <div className="space-y-3 md:col-span-2">
                    <h3 className="text-2xl font-serif text-ink tracking-tight">Acadia &amp; Mount Desert Island</h3>
                    <p className="text-[15px] text-ink-light leading-relaxed">
                      Seal Cove Farm in Lamoine sits on the edge of Acadia
                      National Park&mdash;a rare chance to pair a raw-milk
                      chèvre tasting with a park-loop hike. Asticou Inn in
                      Northeast Harbor puts visitors inside the park gates
                      and at the head of the Asticou Azalea Garden.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-24 md:mt-32 max-w-3xl mx-auto space-y-6">
                <span className="pill-minimal active">Cheese Styles</span>
                <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                  What you&rsquo;ll <span className="italic font-light text-accent">taste</span> on the trail
                </h2>
                <p className="text-base text-ink-light leading-relaxed">
                  Use the filter chips on the Artisans tab to focus an
                  itinerary around a particular style. Five categories
                  dominate production across the current trails.
                </p>
                <dl className="space-y-6 pt-4">
                  <div>
                    <dt className="text-xl font-serif text-ink">Cheddar</dt>
                    <dd className="text-[15px] text-ink-light leading-relaxed mt-1">
                      Vermont&rsquo;s defining style. The farmer-owned Cabot
                      cooperative and Shelburne Farms make the classic
                      two-year clothbound; Grafton and Plymouth push into
                      granular-curd and raw-milk territory. Maine Cheese Guild
                      members like Pineland Farms work in the same idiom.
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xl font-serif text-ink">Alpine</dt>
                    <dd className="text-[15px] text-ink-light leading-relaxed mt-1">
                      Firm, washed-rind wheels in the cooked-curd Jura
                      tradition. Spring Brook Farm&rsquo;s Tarentaise (Reading,
                      VT) and von Trapp Farmstead&rsquo;s Oma (Waitsfield, VT)
                      are the American benchmarks.
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xl font-serif text-ink">Blue</dt>
                    <dd className="text-[15px] text-ink-light leading-relaxed mt-1">
                      Jasper Hill&rsquo;s Bayley Hazen Blue set the bar.
                      Blue Ledge Farm&rsquo;s Middlebury Blue and Green
                      Mountain Blue Cheese round out the category across
                      Vermont&rsquo;s Champlain Valley.
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xl font-serif text-ink">Fresh and soft-ripened goat</dt>
                    <dd className="text-[15px] text-ink-light leading-relaxed mt-1">
                      Vermont Creamery (Websterville), Blue Ledge Farm
                      (Salisbury), Seal Cove (Lamoine, ME), and Appleton
                      Creamery (midcoast Maine) are the major names.
                      Bloomy-rind rounds like Coupole and Bonne Bouche travel
                      nationally.
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xl font-serif text-ink">Sheep-milk cheese</dt>
                    <dd className="text-[15px] text-ink-light leading-relaxed mt-1">
                      Vermont Shepherd&rsquo;s Pyrenees-influenced wheels in
                      Putney are still the defining American sheep cheese.
                      Appleton Creamery makes small sheep-cow blends in
                      Maine.
                    </dd>
                  </div>
                </dl>
              </section>

              <section className="mt-24 md:mt-32 max-w-3xl mx-auto space-y-6">
                <span className="pill-minimal active">Planning</span>
                <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                  When to <span className="italic font-light text-accent">visit</span>
                </h2>
                <p className="text-lg text-ink-light leading-relaxed">
                  Summer and early fall are the peak windows &mdash; most
                  farmstead operations keep their farm stores open Thursday
                  through Sunday in those months. The{' '}
                  <strong className="text-ink font-semibold">Vermont
                  Cheesemakers Festival</strong> in August and the{' '}
                  <strong className="text-ink font-semibold">Maine Cheese
                  Guild&rsquo;s Open Creamery Day</strong> in October are the
                  two anchor events worth planning a weekend around.
                </p>
                <p className="text-lg text-ink-light leading-relaxed">
                  Late fall and winter remain worth the trip for the big
                  retail shops &mdash; Cabot Waterbury, Shelburne Farms
                  Welcome Center, Grafton Brattleboro, Pineland Market
                  &mdash; and for the inns, most of which stay open
                  year-round. Many small farms close their tasting rooms
                  between November and April; always check each
                  cheesery&rsquo;s visitor policy before routing an off-season
                  itinerary through a by-appointment producer.
                </p>
              </section>

              <section className="mt-24 md:mt-32 max-w-3xl mx-auto">
                <div className="space-y-4 mb-10">
                  <span className="pill-minimal active">FAQ</span>
                  <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                    Frequently asked <span className="italic font-light text-accent">questions</span>
                  </h2>
                </div>
                <div className="space-y-3">
                  {FAQ.map((item, i) => (
                    <details
                      key={item.question}
                      className="group border border-border rounded-2xl bg-white/70 hover:bg-white transition-colors"
                      open={i === 0}
                    >
                      <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4">
                        <span className="font-semibold text-ink text-[15px] leading-snug">
                          {item.question}
                        </span>
                        <span
                          aria-hidden="true"
                          className="text-accent text-xl leading-none select-none shrink-0 transition-transform group-open:rotate-45"
                        >
                          +
                        </span>
                      </summary>
                      <div className="px-6 pb-6 -mt-1">
                        <p className="text-[15px] text-ink-light leading-relaxed">
                          {item.answerRich ?? item.answer}
                        </p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </motion.div>
          )}

          {activeTab === 'trails' && (
            <motion.div
              key="trails"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="p-6 md:p-12 max-w-screen-2xl mx-auto w-full"
            >
              <div className="max-w-3xl space-y-4 mb-14">
                <span className="pill-minimal active">Trail Hubs</span>
                <h2 className="text-5xl md:text-6xl font-serif text-ink tracking-tight leading-[1.05]">
                  Choose the <span className="italic font-light text-accent">chapter</span> of your trip.
                </h2>
                <p className="text-lg text-ink-light leading-relaxed">
                  The atlas is now organized by state hubs and route regions.
                  This keeps the guide curated as it grows: state pages set the
                  mood, route regions shape the weekend, and the map remains
                  the connective tissue.
                </p>
              </div>

              <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
                {STATE_HUBS.map((hub) => {
                  const makerCount = CHEESERIES.filter((c) => c.state === hub.state).length;
                  const stayCount = ACCOMMODATIONS.filter((s) => s.state === hub.state).length;
                  const routeCount = REGION_HUBS.filter((region) => region.state === hub.state).length;

                  return (
                    <motion.article
                      key={hub.state}
                      layout
                      className="card-minimal p-8 md:p-10 bg-white/90 relative overflow-hidden group"
                    >
                      <div className="absolute -right-16 -top-16 w-44 h-44 rounded-full bg-accent/5 group-hover:bg-accent/10 transition-colors" />
                      <div className="relative space-y-8">
                        <div className="space-y-4">
                          <span className="pill-minimal active">{hub.eyebrow}</span>
                          <h3 className="text-4xl md:text-5xl font-serif text-ink tracking-tight">
                            {hub.title}
                          </h3>
                          <p className="text-[15px] text-ink-light leading-relaxed max-w-xl">
                            {hub.subtitle}
                          </p>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-sidebar/70 border border-border rounded-2xl p-4">
                            <p className="text-3xl font-serif text-accent">{makerCount}</p>
                            <p className="text-[9px] uppercase tracking-widest text-ink-light font-bold">Makers</p>
                          </div>
                          <div className="bg-sidebar/70 border border-border rounded-2xl p-4">
                            <p className="text-3xl font-serif text-accent">{stayCount}</p>
                            <p className="text-[9px] uppercase tracking-widest text-ink-light font-bold">Stays</p>
                          </div>
                          <div className="bg-sidebar/70 border border-border rounded-2xl p-4">
                            <p className="text-3xl font-serif text-accent">{routeCount}</p>
                            <p className="text-[9px] uppercase tracking-widest text-ink-light font-bold">Routes</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {hub.bestFor.map((item) => (
                            <span key={item} className="px-3 py-1 bg-sidebar text-ink-light text-[9px] font-bold uppercase tracking-widest rounded-full">
                              {item}
                            </span>
                          ))}
                        </div>

                        <div className="flex flex-wrap gap-3 pt-2">
                          {hub.slug && (
                            <Link
                              to={`/${hub.slug}`}
                              className="btn-minimal bg-ink text-white border-ink"
                            >
                              Read the chapter
                            </Link>
                          )}
                          <button
                            onClick={() => exploreState(hub.state, 'cheeses')}
                            className="btn-minimal"
                          >
                            View makers
                          </button>
                          <button
                            onClick={() => exploreState(hub.state, 'stays')}
                            className="btn-minimal"
                          >
                            View stays
                          </button>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </section>

              <section className="space-y-10">
                <div className="max-w-3xl space-y-4">
                  <span className="pill-minimal active">Route Regions</span>
                  <h2 className="text-4xl md:text-5xl font-serif text-ink tracking-tight leading-[1.1]">
                    Smaller routes, <span className="italic font-light text-accent">better weekends.</span>
                  </h2>
                  <p className="text-base text-ink-light leading-relaxed">
                    These are the editorial buckets that keep the guide from
                    becoming a flat directory. Future states can add their own
                    route regions without disturbing the existing trail.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {REGION_HUBS.map((region) => {
                    const stateHub = getStateHub(region.state);

                    return (
                      <motion.article
                        layout
                        key={region.key}
                        className="bg-white/80 border border-border rounded-[28px] p-6 hover:shadow-sm transition-all group"
                      >
                        <div className="space-y-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-2">
                              <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                                {stateHub?.title ?? STATE_LABEL[region.state]}
                              </p>
                              <h3 className="text-3xl font-serif text-ink tracking-tight">
                                {region.shortTitle}
                              </h3>
                            </div>
                            <span className="px-3 py-1 bg-sidebar border border-border rounded-full text-[9px] font-bold uppercase tracking-widest">
                              {region.state}
                            </span>
                          </div>

                          <p className="text-sm text-ink-light leading-relaxed">
                            {region.subtitle}
                          </p>

                          <p className="font-serif italic text-lg text-ink-light leading-snug">
                            {region.mood}
                          </p>

                          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-ink-light font-bold">
                            <span>{region.cheeseIds.length} makers</span>
                            <span className="w-1 h-1 bg-border rounded-full" />
                            <span>{region.stayIds.length} stays</span>
                          </div>

                          <div className="flex flex-wrap gap-3 pt-2">
                            <button
                              onClick={() => exploreRegion(region.key, 'cheeses')}
                              className="btn-minimal"
                            >
                              Explore stops
                            </button>
                            {region.stayIds.length > 0 && (
                              <button
                                onClick={() => exploreRegion(region.key, 'stays')}
                                className="btn-minimal"
                              >
                                Nearby stays
                              </button>
                            )}
                          </div>
                        </div>
                      </motion.article>
                    );
                  })}
                </div>
              </section>
            </motion.div>
          )}

          {activeTab === 'cheeses' && (
            <motion.div
              key="cheeses"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-6 md:p-12 max-w-screen-2xl mx-auto w-full"
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                <div className="space-y-3">
                  <h2 className="text-5xl font-serif text-ink tracking-tight">The <span className="italic font-light text-accent">Artisans</span></h2>
                  <p className="text-sm text-ink-light max-w-md">Hand-picked creameries across {SCOPE.title}, from hillside farmstead caves to production visitor centers.</p>
                </div>

                <div className="flex flex-col gap-3 items-start md:items-end">
                   <div className="flex bg-sidebar rounded-full border border-border p-1 group items-center">
                      <Search className="ml-3 text-ink-light" size={14} />
                      <input
                        type="text"
                        placeholder="Search creameries, towns, styles…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent px-3 py-1 text-xs focus:outline-none placeholder:text-ink-light/50 w-48 md:w-64"
                      />
                   </div>

                   <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                     <button
                       onClick={() => {
                         setStateFilter('');
                         setRegionFilter('');
                       }}
                       className={cn('pill-minimal', stateFilter === '' && regionFilter === '' && 'active')}
                     >All regions</button>
                     {AVAILABLE_STATES.map((s) => (
                       <button
                         key={s}
                         onClick={() => {
                           setStateFilter(stateFilter === s && !regionFilter ? '' : s);
                           setRegionFilter('');
                         }}
                         className={cn('pill-minimal', stateFilter === s && regionFilter === '' && 'active')}
                       >{STATE_LABEL[s]}</button>
                     ))}
                   </div>

                   <div className="flex flex-wrap gap-2 justify-start md:justify-end max-w-3xl">
                     {REGION_HUBS.map((region) => (
                       <button
                         key={region.key}
                         onClick={() => {
                           if (regionFilter === region.key) {
                             setRegionFilter('');
                             setStateFilter('');
                           } else {
                             setRegionFilter(region.key);
                             setStateFilter(region.state);
                           }
                         }}
                         className={cn('pill-minimal', regionFilter === region.key && 'active')}
                       >{region.shortTitle}</button>
                     ))}
                   </div>

                   <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                     <button
                       onClick={() => setStyleFilter('')}
                       className={cn('pill-minimal', styleFilter === '' && 'active')}
                     >All styles</button>
                     {CHEESE_STYLES.map((s) => (
                       <button
                         key={s}
                         onClick={() => setStyleFilter(styleFilter === s ? '' : s)}
                         className={cn('pill-minimal', styleFilter === s && 'active')}
                       >{s}</button>
                     ))}
                   </div>

                   <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                     <button
                       onClick={() => setMilkFilter('')}
                       className={cn('pill-minimal', milkFilter === '' && 'active')}
                     >All milks</button>
                     {MILK_TYPES.map((m) => (
                       <button
                         key={m}
                         onClick={() => setMilkFilter(milkFilter === m ? '' : m)}
                         className={cn('pill-minimal', milkFilter === m && 'active')}
                       >{m}</button>
                     ))}
                   </div>
                </div>
              </div>

              {filteredCheeseries.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">
                  {filteredCheeseries.map((c) => (
                    <motion.div 
                      layout
                      key={c.id} 
                      className="card-minimal group flex flex-col"
                    >
                      <div className="relative aspect-[3/2] overflow-hidden grayscale-[0.5] group-hover:grayscale-0 transition-all duration-700">
                        <img 
                          src={c.image} 
                          alt={c.name} 
                          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                          referrerPolicy="no-referrer"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border border-border">{c.costRange}</span>
                        </div>
                      </div>
                      <div className="p-8 space-y-4 flex-1 flex flex-col">
                        <div className="space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                            {c.city}, {c.state}
                            {getRegionHubForLocation(c.id) && <> &middot; {getRegionHubForLocation(c.id)?.shortTitle}</>}
                          </span>
                          <h3 className="text-2xl font-serif text-ink tracking-tight">{c.name}</h3>
                        </div>
                        <p className="text-sm text-ink-light line-clamp-2 leading-relaxed font-light italic">"{c.description}"</p>
                        
                        <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                          {c.styles.map(s => (
                            <span key={s} className="px-3 py-1 bg-sidebar text-ink-light text-[9px] font-bold uppercase tracking-widest rounded-full">{s}</span>
                          ))}
                        </div>

                        <div className="flex items-center gap-3 pt-6">
                          <button 
                            onClick={() => setSelectedCheesery(c)}
                            className="flex-1 btn-minimal"
                          >
                            Collection details
                          </button>
                          <button 
                            onClick={() => addToItinerary(c.id)}
                            disabled={itinerary.some(item => item.locationId === c.id)}
                            className="p-3 border border-border rounded-full text-ink hover:bg-ink hover:text-white transition-all disabled:opacity-20 disabled:cursor-not-allowed group/btn"
                          >
                            <Plus size={16} className="group-hover/btn:rotate-90 transition-transform" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="py-32 text-center space-y-6 max-w-md mx-auto">
                   <p className="text-ink-light font-serif text-3xl font-light italic">We couldn't find any artisans matching that criteria.</p>
                   <button onClick={clearAllFilters} className="btn-minimal">Clear all search parameters</button>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'stays' && (
            <motion.div
              key="stays"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-6 md:p-12 max-w-screen-2xl mx-auto w-full"
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                <div className="space-y-3">
                  <h2 className="text-5xl font-serif text-ink tracking-tight">Where to <span className="italic font-light text-accent">Rest</span></h2>
                  <p className="text-sm text-ink-light max-w-md">Boutique escapes and authentic dairy farm stays across the region.</p>
                </div>

                <div className="flex flex-wrap gap-2 justify-start md:justify-end max-w-3xl">
                  <button
                    onClick={() => {
                      setStateFilter('');
                      setRegionFilter('');
                    }}
                    className={cn('pill-minimal', stateFilter === '' && regionFilter === '' && 'active')}
                  >
                    All stays
                  </button>
                  {AVAILABLE_STATES.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setStateFilter(stateFilter === s && !regionFilter ? '' : s);
                        setRegionFilter('');
                      }}
                      className={cn('pill-minimal', stateFilter === s && regionFilter === '' && 'active')}
                    >
                      {STATE_LABEL[s]}
                    </button>
                  ))}
                  {REGION_HUBS.filter((region) => region.stayIds.length > 0).map((region) => (
                    <button
                      key={region.key}
                      onClick={() => {
                        if (regionFilter === region.key) {
                          setRegionFilter('');
                          setStateFilter('');
                        } else {
                          setRegionFilter(region.key);
                          setStateFilter(region.state);
                        }
                      }}
                      className={cn('pill-minimal', regionFilter === region.key && 'active')}
                    >
                      {region.shortTitle}
                    </button>
                  ))}
                </div>
              </div>

              {filteredStays.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10">
                  {filteredStays.map((s) => (
                  <div key={s.id} className="card-minimal group">
                    <div className="relative aspect-[3/2] overflow-hidden grayscale-[0.3] group-hover:grayscale-0 transition-all duration-700">
                      <img 
                        src={s.image} 
                        alt={s.name} 
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest border border-border">{s.priceRange}</span>
                      </div>
                    </div>
                    <div className="p-8 space-y-4">
                       <div className="space-y-1">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                            {s.city}, {s.state} &middot; {getRegionHubForLocation(s.id)?.shortTitle ?? s.style}
                          </span>
                          <h3 className="text-2xl font-serif text-ink tracking-tight">{s.name}</h3>
                       </div>
                       <p className="text-sm text-ink-light line-clamp-2 leading-relaxed font-light italic">"{s.description}"</p>
                       
                       <div className="flex items-center gap-3 pt-6">
                          <button 
                            onClick={() => setSelectedStay(s)}
                            className="flex-1 btn-minimal"
                          >
                            Explore residence
                          </button>
                          <button 
                            onClick={() => addToItinerary(s.id)}
                            disabled={itinerary.some(item => item.locationId === s.id)}
                            className="p-3 border border-border rounded-full text-ink hover:bg-ink hover:text-white transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                          >
                            <Plus size={16} />
                          </button>
                       </div>
                    </div>
                  </div>
                  ))}
                </div>
              ) : (
                <div className="py-32 text-center space-y-6 max-w-md mx-auto">
                  <p className="text-ink-light font-serif text-3xl font-light italic">No stays match that trail chapter yet.</p>
                  <button onClick={clearAllFilters} className="btn-minimal">Clear all search parameters</button>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'itinerary' && (
            <motion.div
              key="itinerary"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-6 md:p-12 max-w-screen-2xl mx-auto w-full grid grid-cols-1 xl:grid-cols-3 gap-12"
            >
              <div className="xl:col-span-2 space-y-12">
                <div className="space-y-3">
                  <h2 className="text-5xl font-serif text-ink tracking-tight">Your <span className="italic font-light text-accent">Pilgrimage</span></h2>
                  <p className="text-sm text-ink-light max-w-md">A sequenced path curated for flavor and discovery.</p>
                </div>

                {itinerary.length === 0 ? (
                  <div className="text-center py-32 border border-dashed border-border bg-sidebar/30 rounded-[32px] space-y-6">
                     <Calendar size={48} className="mx-auto text-border" />
                     <div className="space-y-2">
                       <p className="text-ink font-serif text-2xl italic">Your collection is empty.</p>
                       <p className="text-[10px] text-ink-light uppercase tracking-widest font-bold">Begin your journey by adding artisans</p>
                     </div>
                     <button 
                      onClick={() => setActiveTab('cheeses')}
                      className="btn-minimal px-8"
                     >
                       Browse the Trail
                     </button>
                  </div>
                ) : (
                  <div className="space-y-8 relative">
                    {/* Minimal Timeline Line */}
                    <div className="absolute left-6 top-6 bottom-6 w-[1px] border-l border-dashed border-accent z-0" />

                    {itinerary.map((item, idx) => {
                      const location = [...CHEESERIES, ...ACCOMMODATIONS].find(l => l.id === item.locationId);
                      if (!location) return null;

                      return (
                        <motion.div 
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          key={item.id} 
                          className="relative z-10 flex gap-8 group"
                        >
                           <div className="w-12 h-12 rounded-full bg-accent text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-lg group-hover:bg-ink transition-colors">
                              {idx + 1}
                           </div>
                           <div className="flex-1 bg-white p-6 border border-border rounded-2xl hover:shadow-xl transition-all relative overflow-hidden group/card flex flex-col md:flex-row gap-6">
                              <div className="absolute top-4 right-4 flex items-center gap-1 z-10">
                                 <button
                                    onClick={() => moveItinerary(item.id, -1)}
                                    disabled={idx === 0}
                                    className="p-1.5 text-ink-light hover:text-ink disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                    aria-label="Move earlier in route"
                                 >
                                    <ChevronUp size={14} />
                                 </button>
                                 <button
                                    onClick={() => moveItinerary(item.id, 1)}
                                    disabled={idx === itinerary.length - 1}
                                    className="p-1.5 text-ink-light hover:text-ink disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                    aria-label="Move later in route"
                                 >
                                    <ChevronDown size={14} />
                                 </button>
                                 <button
                                    onClick={() => removeFromItinerary(item.id)}
                                    className="p-1.5 text-border hover:text-red-500 transition-colors"
                                    aria-label="Remove from itinerary"
                                 >
                                    <Trash2 size={14} />
                                 </button>
                              </div>

                              <div className="w-full md:w-32 h-32 shrink-0 overflow-hidden rounded-xl">
                                <img 
                                  src={location.image} 
                                  alt={location.name} 
                                  className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110"
                                  referrerPolicy="no-referrer"
                                />
                              </div>
                              <div className="space-y-3 flex-1 min-w-0">
                                 <div className="flex items-center gap-2">
                                    <span className={cn(
                                      "text-[9px] px-2 py-0.5 uppercase tracking-widest font-bold border rounded",
                                      location.type === 'cheesery' ? "border-accent text-accent" : "border-ink text-ink"
                                    )}>
                                      {location.type === 'cheesery' ? 'Cheesery Stop' : 'Residence'}
                                    </span>
                                 </div>
                                 <h4 className="text-2xl font-serif text-ink tracking-tight pr-24">{location.name}</h4>
                                 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-ink-light font-bold uppercase tracking-widest">
                                    <div className="flex items-center gap-1.5 italic">
                                       <span className="w-1.5 h-1.5 bg-border rounded-full" />
                                       <span>{location.city}, {location.state}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <Navigation size={10} className="text-accent" />
                                      <span className="truncate max-w-[240px]">{location.address}</span>
                                    </div>
                                 </div>
                                 <textarea
                                    value={item.notes ?? ''}
                                    onChange={(e) => setItineraryNote(item.id, e.target.value)}
                                    placeholder="Add a note — tasting time, who's driving, reservation confirmation…"
                                    rows={2}
                                    className="w-full mt-2 text-xs bg-sidebar/50 border border-border rounded-lg px-3 py-2 text-ink placeholder:text-ink-light/60 focus:outline-none focus:border-accent resize-y"
                                 />
                              </div>
                           </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {itinerary.length > 0 && (
                <aside className="space-y-8">
                   <div className="bg-sidebar p-8 rounded-[32px] border border-border space-y-6">
                      <h3 className="text-2xl font-serif text-ink tracking-tight">Trip Context</h3>
                      <div className="space-y-4">
                        <div className="p-4 bg-white rounded-2xl border border-border space-y-2">
                          <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Total Stops</p>
                          <p className="text-3xl font-serif text-accent">{itinerary.length}</p>
                        </div>
                        <div className="p-4 bg-white rounded-2xl border border-border space-y-2">
                          <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Route Distance</p>
                          <p className="text-3xl font-serif text-accent">
                            {routeStats.drivingMiles.toFixed(0)} mi
                          </p>
                          <p className="text-[10px] text-ink-light italic">
                            ~{routeStats.miles.toFixed(0)} mi as the crow flies
                          </p>
                        </div>
                        <div className="p-4 bg-white rounded-2xl border border-border space-y-2">
                          <p className="text-[10px] uppercase tracking-widest text-ink-light font-bold">Est. Drive Time</p>
                          <p className="text-3xl font-serif text-accent">
                            {routeStats.hours < 1
                              ? `${Math.round(routeStats.hours * 60)} min`
                              : `${routeStats.hours.toFixed(1)} hr`}
                          </p>
                          <p className="text-[10px] text-ink-light italic">
                            Excludes stop times; budget ~90 min per creamery
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={exportGpx}
                        className="w-full btn-minimal bg-ink text-white py-4 !rounded-2xl flex items-center justify-center gap-3"
                      >
                        <span>Export GPX Route</span>
                        <ChevronRight size={14} />
                      </button>
                   </div>
                   
                   <div className="aspect-square rounded-[32px] overflow-hidden relative border border-border bg-sidebar/50">
                      <Suspense fallback={<MapSkeleton label="Route preview…" />}>
                        <MapView
                          cheeseries={CHEESERIES.filter((c) =>
                            itinerary.some((i) => i.locationId === c.id)
                          )}
                          accommodations={ACCOMMODATIONS.filter((a) =>
                            itinerary.some((i) => i.locationId === a.id)
                          )}
                          fitBounds
                          zoom={8}
                        />
                      </Suspense>
                   </div>
                </aside>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compact in-page footer: appears at the bottom of scrollable content
            rather than pinned, so it never eats viewport height on the map
            or other short tabs. Keeps FTC disclosure + legal links visible
            to anyone who actually scrolls through the page. */}
        <footer className="mt-24 py-4 px-6 md:px-12 border-t border-border bg-sidebar/30">
          <div className="max-w-screen-2xl mx-auto flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] text-ink-light">
            <span className="font-serif italic text-accent font-bold tracking-tighter text-[11px]">Curd Compass</span>
            <a href="/privacy.html" className="uppercase tracking-widest font-bold hover:text-accent transition-colors">Privacy</a>
            <a href="/terms.html" className="uppercase tracking-widest font-bold hover:text-accent transition-colors">Terms</a>
            <a href="/disclosure.html" className="uppercase tracking-widest font-bold hover:text-accent transition-colors">Disclosure</a>
            <span className="ml-auto text-ink-light/70">
              © {new Date().getFullYear()} · Affiliate-monetized via Stay22 ·{' '}
              <a href="mailto:support@bintbiz.com" className="hover:text-accent underline underline-offset-2">advertising</a>
            </span>
          </div>
        </footer>
      </main>

      {/* Details Modal (Cheesery) */}
      <AnimatePresence>
        {selectedCheesery && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               className="absolute inset-0 bg-ink/90 backdrop-blur-md"
               onClick={() => setSelectedCheesery(null)}
             />
             <motion.div 
               initial={{ opacity: 0, scale: 0.98, y: 10 }}
               animate={{ opacity: 1, scale: 1, y: 0 }}
               exit={{ opacity: 0, scale: 0.98, y: 10 }}
               className="bg-bg w-full max-w-6xl max-h-[94vh] overflow-y-auto relative z-10 rounded-[32px] border border-border shadow-2xl flex flex-col lg:flex-row shadow-[0_0_100px_rgba(0,0,0,0.2)]"
             >
                <button 
                  onClick={() => setSelectedCheesery(null)}
                  className="absolute top-6 right-6 p-2 text-ink-light hover:bg-sidebar rounded-full transition-colors z-30 bg-white/80 backdrop-blur-sm border border-border"
                >
                  <X size={20} />
                </button>

                <div className="lg:w-2/5 h-80 lg:h-auto relative overflow-hidden group border-r border-border">
                  <img 
                    src={selectedCheesery.image} 
                    alt={selectedCheesery.name}
                    className="w-full h-full object-cover grayscale-[0.2]"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                
                <div className="flex-1 p-8 lg:p-16 space-y-12">
                   <div className="space-y-4">
                      <span className="pill-minimal active">Artisan Spotlight</span>
                      <h2 className="text-6xl font-serif text-ink tracking-tight">{selectedCheesery.name}</h2>
                      <p className="text-xl text-ink-light italic font-light">{selectedCheesery.city}, {STATE_LABEL[selectedCheesery.state]}</p>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                      <div className="space-y-6">
                         <div className="space-y-4">
                           <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent">The Legacy</h4>
                           <p className="text-ink-light leading-relaxed font-serif text-lg italic">"{selectedCheesery.history}"</p>
                         </div>
                         
                         <div className="space-y-4 pt-4">
                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent">Pairing Notes</h4>
                            <div className="flex flex-wrap gap-2 text-sm italic text-ink-light font-serif">
                               {selectedCheesery.pairings.map(p => (
                                 <span key={p} className="flex items-center gap-2">
                                   <span className="w-1 h-1 bg-border rounded-full" />
                                   {p}
                                 </span>
                               ))}
                            </div>
                         </div>
                      </div>

                      <div className="bg-sidebar/50 p-8 rounded-2xl border border-border space-y-6">
                         <div className="space-y-1">
                           <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Availability</p>
                           <p className="text-sm text-ink">{selectedCheesery.hours}</p>
                         </div>
                         <div className="space-y-1">
                           <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Address</p>
                           <p className="text-sm text-ink">{selectedCheesery.address}</p>
                         </div>
                         <div className="space-y-1">
                           <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Experience</p>
                           <p className="text-sm text-ink">{selectedCheesery.tastingAvailable ? "Guided Tastings Available" : "Gallery/Retail Only"}</p>
                         </div>
                         
                         <div className="pt-4">
                            <button 
                              onClick={() => { addToItinerary(selectedCheesery.id); setSelectedCheesery(null); }}
                              disabled={itinerary.some(item => item.locationId === selectedCheesery.id)}
                              className="w-full btn-minimal bg-ink text-white py-4 !rounded-xl"
                            >
                              {itinerary.some(item => item.locationId === selectedCheesery.id) ? "Already in route" : "Add to journey"}
                            </button>
                         </div>
                      </div>
                   </div>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

       {/* Details Modal (Stay) */}
       <AnimatePresence>
        {selectedStay && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               exit={{ opacity: 0 }}
               className="absolute inset-0 bg-ink/90 backdrop-blur-md"
               onClick={() => setSelectedStay(null)}
             />
             <motion.div 
               initial={{ opacity: 0, scale: 0.98, y: 10 }}
               animate={{ opacity: 1, scale: 1, y: 0 }}
               exit={{ opacity: 0, scale: 0.98, y: 10 }}
               className="bg-bg w-full max-w-6xl max-h-[94vh] overflow-y-auto relative z-10 rounded-[32px] border border-border shadow-2xl flex flex-col lg:flex-row shadow-[0_0_100px_rgba(0,0,0,0.2)]"
             >
                <button 
                  onClick={() => setSelectedStay(null)}
                  className="absolute top-6 right-6 p-2 text-ink-light hover:bg-sidebar rounded-full transition-colors z-30 bg-white/80 backdrop-blur-sm border border-border"
                >
                  <X size={20} />
                </button>

                <div className="lg:w-2/5 h-80 lg:h-auto relative overflow-hidden group border-r border-border">
                  <img 
                    src={selectedStay.image} 
                    alt={selectedStay.name}
                    className="w-full h-full object-cover grayscale-[0.2]"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
                
                <div className="flex-1 p-8 lg:p-16 space-y-12">
                   <div className="space-y-4">
                      <span className="pill-minimal active">Accommodation Scout</span>
                      <h2 className="text-6xl font-serif text-ink tracking-tight">{selectedStay.name}</h2>
                      <p className="text-xl text-ink-light italic font-light">{selectedStay.city}, {STATE_LABEL[selectedStay.state]} • {selectedStay.style}</p>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                      <div className="space-y-8">
                         <div className="space-y-4">
                           <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent">The Stay</h4>
                           <p className="text-ink-light leading-relaxed font-serif text-lg italic">"{selectedStay.description}"</p>
                         </div>
                         
                         <div className="space-y-4">
                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-accent">Amenities & Charms</h4>
                            <div className="grid grid-cols-1 gap-2 text-sm italic text-ink-light font-serif">
                               {selectedStay.amenities.map(a => (
                                 <div key={a} className="flex items-center gap-2">
                                   <span className="w-1 h-1 bg-border rounded-full" />
                                   {a}
                                 </div>
                               ))}
                            </div>
                         </div>
                      </div>

                      <div className="bg-sidebar/50 p-8 rounded-2xl border border-border space-y-6">
                         <div className="grid grid-cols-2 gap-6">
                           <div className="space-y-1">
                             <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Check-In</p>
                             <p className="text-sm font-bold text-ink">{selectedStay.hours}</p>
                           </div>
                           <div className="space-y-1">
                             <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Price tier</p>
                             <p className="text-sm font-bold text-ink">{selectedStay.priceRange}</p>
                           </div>
                         </div>
                         <div className="space-y-1">
                           <p className="text-[10px] font-bold uppercase tracking-widest text-ink-light">Pet policy</p>
                           <p className="text-sm font-bold text-ink">{selectedStay.petFriendly ? "Dogs welcomed" : "No pets permitted"}</p>
                         </div>
                         
                         <div className="pt-4 space-y-4">
                            <button 
                              onClick={() => { addToItinerary(selectedStay.id); setSelectedStay(null); }}
                              disabled={itinerary.some(item => item.locationId === selectedStay.id)}
                              className="w-full btn-minimal bg-ink text-white py-4 !rounded-xl"
                            >
                              {itinerary.some(item => item.locationId === selectedStay.id) ? "Already curating" : "Add to itinerary"}
                            </button>
                            {/* Booking CTA points at a Booking.com search keyed
                                on the property's name — a Stay22 LMA partner, so
                                the script in index.html transparently rewrites
                                this into an affiliate URL on click. Linking to
                                the property's own site (hotelvt.com, etc.) would
                                leak every reservation outside our monetization
                                path, so we deliberately never do that here. */}
                            <a
                              href={bookingSearchUrl(selectedStay)}
                              target="_blank"
                              rel="noreferrer noopener sponsored"
                              data-stay22="book"
                              className="block text-center btn-minimal py-4 !rounded-xl bg-accent text-white border-accent hover:bg-ink hover:border-ink"
                            >
                              Find best rates
                            </a>
                         </div>
                      </div>
                   </div>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>

          </div>
        </div>
      );
    }
